import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useApiResource } from '../hooks/useApiResource';
import { DetailPageLayout, DetailPageState } from '../components/DetailPageLayout';
import { getCaseCategoryLabel } from '../lib/contentLabels';

export default function CaseDetail() {
  const { slug } = useParams();
  const { i18n } = useTranslation();
  const { data: caseItem, loading, error } = useApiResource(`/cases/${slug}`, { lang: i18n.language });

  const getLocalizedField = (item, field) => {
    if (i18n.language === 'en' && item[`${field}_en`]) {
      return item[`${field}_en`];
    }
    return item[field];
  };

  if (loading) {
    return <DetailPageState>Loading...</DetailPageState>;
  }

  if (!caseItem) {
    return <DetailPageState>{error ? 'Failed to load case.' : 'Case not found.'}</DetailPageState>;
  }

  const title = getLocalizedField(caseItem, 'title');
  const description = getLocalizedField(caseItem, 'description');
  const challenge = getLocalizedField(caseItem, 'challenge');
  const solution = getLocalizedField(caseItem, 'solution');
  const results = getLocalizedField(caseItem, 'results') || [];
  const categoryLabel = getCaseCategoryLabel(caseItem.category, i18n.language);
  const labels = i18n.language === 'en'
    ? { back: 'Back to cases', challenge: 'Challenge', solution: 'Solution', results: 'Results', client: 'Client' }
    : { back: 'Назад к кейсам', challenge: 'Задача', solution: 'Решение', results: 'Результаты', client: 'Клиент' };

  return (
    <DetailPageLayout backTo="/cases" backLabel={labels.back}>
        <div className="glass detail-page-card">
          <div className="relative h-72 md:h-96">
            <img src={caseItem.image_url} alt={title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
          </div>
          <div className="p-8 md:p-10 space-y-6">
            <div className="text-xs uppercase tracking-widest text-[#E11D2E]">{categoryLabel}</div>
            <h1 className="detail-page-title">{title}</h1>
            <p className="detail-page-body whitespace-pre-line">{description}</p>
            <div className="detail-page-meta">
              {labels.client}: <span className="text-white">{caseItem.client}</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="glass rounded-2xl p-5">
                <h2 className="text-xl font-bold text-white mb-3">{labels.challenge}</h2>
                <p className="text-white/70 whitespace-pre-line">{challenge}</p>
              </div>
              <div className="glass rounded-2xl p-5">
                <h2 className="text-xl font-bold text-white mb-3">{labels.solution}</h2>
                <p className="text-white/70 whitespace-pre-line">{solution}</p>
              </div>
            </div>

            {Array.isArray(results) && results.length > 0 && (
              <div className="glass rounded-2xl p-5">
                <h2 className="text-xl font-bold text-white mb-3">{labels.results}</h2>
                <ul className="list-disc list-inside text-white/70 space-y-2">
                  {results.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
    </DetailPageLayout>
  );
}
